import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ConsentmanagerDeAdapter, ConsentmanagerDeConfig } from './adapters/ConsentmanagerDeAdapter';
import { UsercentricsAdapter, UsercentricsConfig } from './adapters/UsercentricsAdapter';
import { ConsentManager, ConsentManagerVendor, ConsentRequirement, ConsentState } from './consent.types';

export type ConsentConfig = {
    enabled: boolean;
    vendor: ConsentManagerVendor; // 'usercentrics' | 'consentmanagerde' | 'none'
    vendorConfig?: Record<string, any>;
};

function buildManager(vendor: ConsentManagerVendor): ConsentManager | null {
    switch (vendor) {
        case 'usercentrics':
            return new UsercentricsAdapter();
        case 'consentmanagerde':
            return new ConsentmanagerDeAdapter();
        default:
            return null;
    }
}

export type ConsentContextValue = {
    managerName: ConsentManagerVendor;
    ready: boolean;
    state: ConsentState;
    hasConsent: (req: ConsentRequirement) => boolean;
    showBanner: () => void;
};

const ConsentContext = createContext<ConsentContextValue>({
    managerName: 'none',
    ready: false,
    state: { services: {}, categories: {} },
    hasConsent: () => false,
    showBanner: () => {},
});

export const ConsentProvider: React.FC<React.PropsWithChildren<{ config: ConsentConfig }>> = ({ config, children }) => {
    const managerRef = useRef<ConsentManager | null>(null);
    const [ready, setReady] = useState(false);
    const [state, setState] = useState<ConsentState>({ services: {}, categories: {} });

    useEffect(() => {
        let active = true;
        let unsubscribe: undefined | (() => void);
        (async () => {
            if (!config?.enabled) {
                setReady(true);
                return;
            }
            const mgr = buildManager(config.vendor);
            managerRef.current = mgr;
            if (!mgr) {
                setReady(true);
                return;
            }
            await mgr.load((config.vendorConfig ?? {}) as UsercentricsConfig | ConsentmanagerDeConfig);
            if (!active) return;
            setReady(mgr.isReady());
            setState(mgr.getState());
            unsubscribe = mgr.onChange((s) => setState(s));
        })();
        return () => {
            active = false;
            unsubscribe?.();
        };
    }, [config?.enabled, config?.vendor, JSON.stringify(config?.vendorConfig)]);

    // Manager-Name deterministisch aus der Config ableiten (robuster als Ref lesen)
    const currentManagerName: ConsentManagerVendor = config?.enabled ? config.vendor : 'none';

    const value = useMemo<ConsentContextValue>(
        () => ({
            managerName: currentManagerName,
            ready,
            state,
            hasConsent: (req: ConsentRequirement) => {
                if (!config?.enabled) return true; // Feature aus = alles erlaubt
                const mgr = managerRef.current;
                if (!mgr) return false;
                return mgr.hasConsent(req);
            },
            showBanner: () => managerRef.current?.showBanner(),
        }),
        [currentManagerName, ready, state, config?.enabled],
    );

    return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
};

export const useConsent = () => useContext(ConsentContext);
